import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "../../../../lib/network/api";

// ==================== INTERFACES ====================

export interface ProfileStatus {
  completionPercentage: number;
  isFullyVerified: boolean;
  verificationSteps: { name: string; completed: boolean }[];
  pendingSteps: string[];
  completedSteps: string[];
}

export interface ContributorStats {
  totalProjects: number;
  activeProjects: number;
  completedProjects: number;
  pendingApplications: number;
  totalEarnings: number;
  pendingEarnings: number;
  availableBalance: number;
  hoursWorked: number;
  tasksCompleted: number;
  averageRating: number;
  successRate: number;
  daysAsContributor: number;
  totalApplications: number;
}

export interface ProjectStats {
  total: number;
  active: number;
  completed: number;
  pending: number;
  approved: number;
  rejected: number;
  terminated: number;
}

export interface Earnings {
  totalEstimated: number;
  fromCompleted: number;
  fromActive: number;
  availableBalance: number;
  pendingBalance: number;
  avgHourlyRate: number;
  totalHoursWorked: number;
}

export interface LevelInfo {
  currentLevel: number;
  currentXP: number;
  xpToNextLevel: number;
  progressPercentage: number;
}

export interface ActiveProject {
  _id: string;
  projectId: string;
  projectTitle: string;
  projectCategory: string;
  projectDifficulty: string;
  status: string;
  hourlyRate: number;
  estimatedHours: string;
  startedAt: string | null;
  assignedAt: string;
}

export interface RecentApplication {
  _id: string;
  projectId: string;
  projectTitle: string;
  projectCategory: string;
  status: string;
  appliedAt: string;
  reviewedAt: string | null;
  hourlyRate: number;
}

export interface RecentActivity {
  _id: string;
  type: string;
  title: string;
  description: string;
  timestamp: string;
  metadata: Record<string, any>;
}

export interface Notification {
  _id: string;
  type: string;
  title: string;
  message: string;
  isRead: boolean;
  actionUrl: string | null;
  createdAt: string;
}

export interface AvailableProjects {
  total: number;
  byCategory: Record<string, number>;
  byDifficulty: Record<string, number>;
  newThisWeek: number;
  topCategories: { name: string; count: number }[];
}

export interface PendingTask {
  _id: string;
  title: string;
  description: string;
  priority: string;
  dueDate: string | null;
  reward: string | null;
  type: string;
}

export interface UpcomingDeadline {
  _id: string;
  projectId: string;
  projectTitle: string;
  dueDate: string;
  priority: string;
  status: string;
}

export interface ContributorDashboardData {
  user: {
    firstName: string;
    lastName: string;
    email: string;
    status: string;
    memberSince: string;
  };
  profile: ProfileStatus;
  stats: ContributorStats;
  projectStats: ProjectStats;
  earnings: Earnings;
  levelInfo: LevelInfo;
  activeProjects: ActiveProject[];
  recentApplications: RecentApplication[];
  recentActivities: RecentActivity[];
  availableProjects: AvailableProjects;
  notifications: Notification[];
  unreadNotificationCount: number;
  pendingTasks: PendingTask[];
  upcomingDeadlines: UpcomingDeadline[];
}

export interface EarningsChartData {
  labels: string[];
  data: number[];
}

// ==================== API FUNCTIONS ====================

export const getContributorDashboard = async (
  userId: string,
  params?: { period?: string }
): Promise<ContributorDashboardData> => {
  const response: any = await api.get(`/contributor/dashboard/${userId}`, {
    ...params,
  });
  return response.data?.data || response.data;
};

export const getContributorActivities = async (
  userId: string,
  params?: { limit?: number; offset?: number; type?: string }
): Promise<{ activities: RecentActivity[]; total: number }> => {
  const response: any = await api.get(
    `/contributor/dashboard/${userId}/activities`,
    {
      ...params,
    }
  );
  return response.data?.data || response.data;
};

export const getEarningsChart = async (
  userId: string,
  params?: { period?: string }
): Promise<EarningsChartData> => {
  const response: any = await api.get(
    `/contributor/dashboard/${userId}/earnings-chart`,
    {
      ...params,
    }
  );
  return response.data?.data || response.data;
};

export const getContributorNotifications = async (
  userId: string,
  params?: { limit?: number; type?: string; unreadOnly?: boolean }
): Promise<{
  notifications: Notification[];
  total: number;
  unread: number;
}> => {
  const response: any = await api.get(
    `/contributor/dashboard/${userId}/notifications`,
    {
      ...params,
    }
  );
  return response.data?.data || response.data;
};

export const getUnreadNotificationCount = async (
  userId: string
): Promise<{ count: number }> => {
  const response: any = await api.get(
    `/contributor/dashboard/${userId}/notifications/unread-count`,
    { headers: { "Cache-Control": "no-cache" } }
  );
  return response.data?.data || response.data;
};

export const markNotificationAsRead = async (
  userId: string,
  notificationId: string
): Promise<{ success: boolean }> => {
  const response: any = await api.patch(
    `/contributor/dashboard/${userId}/notifications/${notificationId}/read`
  );
  return response.data?.data || response.data;
};

export const markAllNotificationsAsRead = async (
  userId: string,
  type?: string
): Promise<{ modifiedCount: number }> => {
  const response: any = await api.patch(
    `/contributor/dashboard/${userId}/notifications/read-all`,
    { type }
  );
  return response.data?.data || response.data;
};

// ==================== REACT QUERY HOOKS ====================

export const useGetContributorDashboard = (
  userId: string,
  params?: { period?: string }
) => {
  return useQuery({
    queryKey: ["contributor-dashboard", userId, params],
    queryFn: () => getContributorDashboard(userId, params),
    enabled: !!userId,
    retry: 2,
    refetchOnWindowFocus: false,
  });
};

export const useGetContributorActivities = (
  userId: string,
  params?: { limit?: number; offset?: number; type?: string }
) => {
  return useQuery({
    queryKey: ["contributor-activities", userId, params],
    queryFn: () => getContributorActivities(userId, params),
    enabled: !!userId,
    retry: 2,
    refetchOnWindowFocus: false,
  });
};

export const useGetEarningsChart = (
  userId: string,
  params?: { period?: string }
) => {
  return useQuery({
    queryKey: ["contributor-earnings-chart", userId, params],
    queryFn: () => getEarningsChart(userId, params),
    enabled: !!userId,
    retry: 2,
    refetchOnWindowFocus: false,
  });
};

export const useGetContributorNotifications = (
  userId: string,
  params?: { limit?: number; type?: string; unreadOnly?: boolean }
) => {
  return useQuery({
    queryKey: ["contributor-notifications", userId, params],
    queryFn: () => getContributorNotifications(userId, params),
    enabled: !!userId,
    retry: 2,
    refetchOnWindowFocus: false,
  });
};

export const useGetUnreadNotificationCount = (userId: string) => {
  return useQuery({
    queryKey: ["contributor-unread-count", userId],
    queryFn: () => getUnreadNotificationCount(userId),
    enabled: !!userId,
    refetchInterval: 30000, // Refetch every 30 seconds
  });
};

export const useMarkNotificationAsRead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      userId,
      notificationId,
    }: {
      userId: string;
      notificationId: string;
    }) => markNotificationAsRead(userId, notificationId),
    onSuccess: (_, { userId }) => {
      queryClient.invalidateQueries({
        queryKey: ["contributor-dashboard", userId],
      });
      queryClient.invalidateQueries({
        queryKey: ["contributor-notifications", userId],
      });
      queryClient.invalidateQueries({
        queryKey: ["contributor-unread-count", userId],
      });
    },
  });
};

export const useMarkAllNotificationsAsRead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, type }: { userId: string; type?: string }) =>
      markAllNotificationsAsRead(userId, type),
    onSuccess: (_, { userId }) => {
      queryClient.invalidateQueries({
        queryKey: ["contributor-dashboard", userId],
      });
      queryClient.invalidateQueries({
        queryKey: ["contributor-notifications", userId],
      });
      queryClient.invalidateQueries({
        queryKey: ["contributor-unread-count", userId],
      });
    },
  });
};
